import { appLocalStorage, isStorageKey } from '../utils/storage';
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services';
import config from '../utils/config';
import { getPrivatFlg, setLocalStorage } from '../utils/func';
import { readGuestOpinion, recordGuestVote, subscribeGuestTrial } from '../guestTrial/store';

const validScore = (score) => Number.isInteger(Number(score)) && Number(score) >= 1 && Number(score) <= 7;
const validId = (id) => Number.isSafeInteger(Number(id)) && Number(id) > 0;
const readPrivacy = () => {
  try { return getPrivatFlg(); } catch { return false; }
};
const sameCard = (scope, props) => scope.reactionId === props.reactionId
  && scope.storyId === props.storyId && scope.authenticated === Boolean(props.authenticated);

// Reaction cards are remounted during feed navigation. Keep accepted writes
// outside their lifecycle so an older card cannot overwrite a newer vote.
let inFlightVote = null;
const pendingVotes = [];
const voteWaiters = new Set();
// The editor may open immediately, but hydrates/saves only after accepted
// slider writes finish. Otherwise a late vote can overwrite its score/privacy
// or recreate an opinion after Delete. Wait only for this account and event.
export function waitForFeedVotes(storyId, signal) {
  const session = readSession();
  return new Promise(resolve => {
    const finish = () => { voteWaiters.delete(check); signal?.removeEventListener('abort', finish); resolve(); };
    const check = () => {
      const matches = work => work && work.session === session
        && (storyId === null || Number(work.scope.storyId) === Number(storyId));
      if (signal?.aborted || (!matches(inFlightVote) && !pendingVotes.some(matches))) finish();
    };
    voteWaiters.add(check);
    signal?.addEventListener('abort', finish, { once: true });
    check();
  });
}
// Opening one's profile must see slider gestures already accepted by the UI.
export const waitForAllFeedVotes = signal => waitForFeedVotes(null, signal);
const hasPending = (scope) => pendingVotes.some((work) => work.scope === scope);
const removePending = (scope) => {
  for (let index = pendingVotes.length - 1; index >= 0; index -= 1) {
    if (pendingVotes[index].scope === scope) pendingVotes.splice(index, 1);
  }
};
const readSession = () => {
  try { return appLocalStorage.getItem(config.token); } catch { return null; }
};

async function drainVotes() {
  if (inFlightVote || !pendingVotes.length) return;
  const work = pendingVotes.shift();
  inFlightVote = work;
  let response;
  try {
    // A queued gesture belongs to the session that accepted it. Never send
    // an old user's vote with a different account's newly installed token.
    response = work.session === readSession()
      ? await api.reactionSaveVoting(work.payload)
      : { success: false, statusCode: 401 };
  } catch {
    response = { success: false };
  }
  try {
    if (Number(response?.statusCode) === 401 || response?.cancelled) removePending(work.scope);
    work.complete(response, hasPending(work.scope));
  } finally {
    inFlightVote = null;
    drainVotes();
    voteWaiters.forEach(check => check());
  }
}

function enqueueVote(work) {
  removePending(work.scope);
  if (inFlightVote?.scope !== work.scope || inFlightVote.payload.score !== work.payload.score
    || inFlightVote.payload.privat !== work.payload.privat) pendingVotes.push(work);
  drainVotes();
}

function createScope(props) {
  const guestOpinion = !props.authenticated && readGuestOpinion(props.storyId);
  return {
    reactionId: props.reactionId,
    storyId: props.storyId,
    authenticated: Boolean(props.authenticated),
    session: readSession(),
    view: {
      active: false,
      score: guestOpinion?.score ?? (validScore(props.initialScore) ? Number(props.initialScore) : 4),
      privateOpinion: typeof props.initialPrivacy === 'boolean' ? props.initialPrivacy : readPrivacy(),
      saving: false,
      error: false,
      ownReactionId: null,
    },
  };
}

// A card owns its draft. Only the latest completed slider gesture / privacy
// choice is queued, and each POST finishes before the next one starts.
export default function useFeedVote(props) {
  const { reactionId, storyId, authenticated } = props;
  const session = readSession();
  const propsRef = useRef(props);
  propsRef.current = props;
  const scopeRef = useRef(null);
  if (!scopeRef.current) scopeRef.current = createScope(props);
  const [state, setState] = useState(() => ({ scope: scopeRef.current, ...scopeRef.current.view }));
  const mounted = useRef(false);

  const isCurrent = useCallback((scope) => mounted.current && scopeRef.current === scope
    && sameCard(scope, propsRef.current) && scope.session === readSession(), []);
  const synchronizeSession = useCallback(() => {
    if (!mounted.current || scopeRef.current.session === readSession()) return;
    const scope = createScope(propsRef.current);
    scopeRef.current = scope;
    setState({ scope, ...scope.view });
  }, []);
  const publish = useCallback((scope, patch) => {
    if (!isCurrent(scope)) return;
    scope.view = { ...scope.view, ...patch };
    setState({ scope, ...scope.view });
  }, [isCurrent]);

  useEffect(() => {
    mounted.current = true;
    const onStorage = (event) => {
      if (isStorageKey(event, config.token)) synchronizeSession();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      mounted.current = false;
      window.removeEventListener('storage', onStorage);
    };
  }, [synchronizeSession]);

  useEffect(() => {
    const scope = createScope(propsRef.current);
    scopeRef.current = scope;
    setState({ scope, ...scope.view });
  }, [reactionId, storyId, authenticated, session]);

  useEffect(() => {
    if (authenticated) return undefined;
    return subscribeGuestTrial(() => {
      const scope = scopeRef.current;
      if (!scope.authenticated) publish(scope, { score: readGuestOpinion(scope.storyId)?.score ?? 4 });
    });
  }, [authenticated, publish]);

  const changeScore = useCallback((value) => {
    const scope = scopeRef.current;
    if (!validScore(value)) return;
    publish(scope, { score: Number(value), active: scope.authenticated });
  }, [publish]);

  const saveScore = useCallback((value) => {
    const scope = scopeRef.current;
    if (!isCurrent(scope)) {
      synchronizeSession();
      return;
    }
    // A standalone event (the unsubscribe entry) has no parent opinion. Null
    // explicitly means absent; invalid numeric ids still fail validation.
    if (!validScore(value) || !validId(scope.storyId) || (scope.authenticated && scope.reactionId !== null && !validId(scope.reactionId))) {
      publish(scope, { error: true });
      return;
    }
    const score = Number(value);
    if (!scope.authenticated) {
      const trial = recordGuestVote(scope.storyId, score);
      publish(scope, { active: false, score: trial.opinion?.score ?? 4, error: false, saving: false });
      propsRef.current.onGuestTrial?.(trial);
      if (!trial.accepted && trial.limitReached) propsRef.current.onGuestLimit?.(trial);
      return;
    }
    publish(scope, { active: true, score, error: false, saving: true });
    const payload = {
      story_id: Number(scope.storyId),
      ...(scope.reactionId !== null ? { parent_reaction: Number(scope.reactionId) } : {}),
      score,
      privat: scope.view.privateOpinion,
    };
    enqueueVote({
      scope, payload, session: scope.session,
      complete(response, queued) {
        if (!isCurrent(scope)) {
          synchronizeSession();
          return;
        }
        if (Number(response?.statusCode) === 401) {
          publish(scope, { saving: false, error: true });
          propsRef.current.onUnauthorized?.();
        } else if (response?.cancelled) {
          publish(scope, { saving: false, error: false, active: false });
        } else if (!response?.success) {
          publish(scope, { saving: queued, error: !queued });
        } else {
          publish(scope, { saving: queued, error: false,
            ...(validId(response.id) ? { ownReactionId: Number(response.id) } : {}) });
        }
      },
    });
  }, [isCurrent, publish, synchronizeSession]);

  const togglePrivacy = useCallback(() => {
    const scope = scopeRef.current;
    if (!isCurrent(scope)) return;
    if (!scope.authenticated) {
      propsRef.current.onUnauthorized?.();
      return;
    }
    const privateOpinion = !scope.view.privateOpinion;
    try { setLocalStorage(config.privat, privateOpinion); } catch { /* The choice still applies to this vote. */ }
    publish(scope, { privateOpinion });
    saveScore(scope.view.score);
  }, [isCurrent, publish, saveScore]);

  const retry = useCallback(() => saveScore(scopeRef.current.view.score), [saveScore]);
  const reset = useCallback(() => {
    if (!isCurrent(scopeRef.current)) return;
    const previous = scopeRef.current;
    const scope = createScope(propsRef.current);
    scope.view = { ...scope.view, score: previous.view.score, privateOpinion: previous.view.privateOpinion,
      ownReactionId: previous.view.ownReactionId };
    scopeRef.current = scope;
    setState({ scope, ...scope.view });
  }, [isCurrent]);

  const view = state.scope === scopeRef.current && sameCard(state.scope, props) && state.scope.session === session
    ? state : createScope(props).view;
  return {
    active: view.active, score: view.score, privateOpinion: view.privateOpinion,
    saving: view.saving, error: view.error, ownReactionId: view.ownReactionId,
    changeScore, saveScore, togglePrivacy, retry, reset,
  };
}
